import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0, style = {} }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

const values = [
  { title: 'Transparency first', body: 'Every fee, every rule, every payout formula is published openly. No hidden mechanics.' },
  { title: 'Built for Kenya', body: 'M-PESA native, KES denominated, markets that reflect what Kenyans actually care about.' },
  { title: 'Responsible by design', body: 'Deposit limits, self-exclusion, and cooling periods are built in from day one.' },
  { title: 'Long-term thinking', body: 'We are pursuing CMA regulatory approval. Built to last — not to exploit a grey area.' },
];

export default function About() {
  const navigate = useNavigate();
  const [heroRef, heroVisible] = useInView(0.05);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>About — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Mino Markets is Kenya's prediction markets platform, built by Martin W. David." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        /* ── Aurora beam background ── */
        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55, 114, 255, 0.09) 40deg,
            rgba(22, 199, 132, 0.07) 80deg,
            rgba(124, 58, 237, 0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22, 199, 132, 0.06) 50deg,
            rgba(55, 114, 255, 0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }

        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }

        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }

        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);   opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }
        @keyframes shimmer {
          0%,100% { opacity: 0.4; }
          50%      { opacity: 1; }
        }

        /* ── Nav ── */
        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; letter-spacing: 0.01em; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        /* ── Cards ── */
        .mino-val-card { transition: border-color 0.3s ease, background 0.3s ease; }
        .mino-val-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.05) !important; }

        .mino-cta-dark:hover { opacity: 0.82; }
        .mino-cta-ghost:hover { border-color: rgba(255,255,255,0.28) !important; color: rgba(255,255,255,0.68) !important; }

        .mino-tl-row:hover .mino-tl-dot { background: #e8e6e0 !important; box-shadow: 0 0 8px rgba(55,114,255,0.6) !important; }

        /* ── Stat scan line ── */
        .mino-stat { position: relative; overflow: hidden; }
        .mino-stat::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(90deg, transparent 0%, rgba(55,114,255,0.06) 50%, transparent 100%);
          transform: translateX(-100%);
          animation: scanLine 6s ease-in-out infinite;
        }
        @keyframes scanLine {
          0%   { transform: translateX(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `}</style>

      {/* Background layers */}
      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[['Markets', '/markets'], ['Investors', '/investors'], ['Blog', '/blog']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button onClick={() => navigate('/contact')} style={{ background: '#e8e6e0', color: '#000', border: 'none', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500, cursor: 'pointer', letterSpacing: '-0.1px', transition: 'opacity 0.2s' }} className="mino-cta-dark">
          Get in touch
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Hero */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>About Mino</p>
            <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(48px,7vw,88px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 36, maxWidth: 720 }}>
              A prediction markets<br />platform built for<br /><span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)' }}>Africa.</span>
            </h1>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300 }}>
              Kenya's first real-money prediction markets platform. We believe Africans deserve the same financial tools that exist everywhere else.
            </p>
          </div>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Mission */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 24 }}>The Mission</p>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(30px,3.5vw,44px)', fontWeight: 600, lineHeight: 1.12, letterSpacing: '-1.2px', color: '#e8e6e0', marginBottom: 28 }}>
                  Markets that reflect<br />the world Kenyans live in.
                </h2>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, marginBottom: 18, fontWeight: 300 }}>
                  Prediction markets aggregate collective intelligence about the future. They've existed in the US and Europe for years — but never properly in Kenya or East Africa.
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, color: 'rgba(255,255,255,0.34)', lineHeight: 1.85, fontWeight: 300 }}>
                  Mino changes that. Built on M-PESA, denominated in KES, with markets on Kenyan politics, sports, and global events.
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[['Founded', '2024'], ['Headquarters', 'Nairobi, Kenya'], ['Payment', 'M-PESA'], ['Currency', 'KES']].map(([l, v], i, a) => (
                  <div key={l} className="mino-stat" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0', borderBottom: i < a.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.2)', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{l}</span>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, color: '#e8e6e0', fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Values */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>What We Stand For</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              {values.map(({ title, body }, i) => (
                <div key={title} className="mino-val-card" style={{ padding: '40px 36px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.025)', borderRadius: i === 0 ? '16px 0 0 0' : i === 1 ? '0 16px 0 0' : i === 2 ? '0 0 0 16px' : '0 0 16px 0' }}>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, color: '#e8e6e0', marginBottom: 12, letterSpacing: '-0.2px' }}>{title}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.8, fontWeight: 300 }}>{body}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Timeline */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>How We Got Here</p>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {[
                ['2024', 'Concept & build', 'Martin begins building Mino from scratch — frontend, backend, payments, market mechanics.'],
                ['Early 2025', 'Infrastructure', 'Platform moved to production. M-PESA PayBill application submitted under DAFCOM ENTERPRISES.'],
                ['Mid 2025', 'M-PESA live', 'Safaricom approves PayBill. B2C withdrawals confirmed working in production.'],
                ['Late 2025', 'Security audit', 'Full pre-launch audit across 74 files. Pricing, tax, and security all hardened.'],
                ['2026', 'Launch', "Kenya's first real-money prediction markets platform goes live."],
              ].map(([year, event, detail], i, a) => (
                <div key={year} className="mino-tl-row" style={{ display: 'grid', gridTemplateColumns: '110px 1px 1fr', gap: '0 36px', paddingBottom: i < a.length - 1 ? 44 : 0 }}>
                  <div style={{ textAlign: 'right', paddingTop: 2 }}>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.04em' }}>{year}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div className="mino-tl-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: 'rgba(255,255,255,0.18)', flexShrink: 0, marginTop: 4, transition: 'background 0.25s, box-shadow 0.25s' }} />
                    {i < a.length - 1 && <div style={{ flex: 1, width: 1, background: 'rgba(255,255,255,0.06)', marginTop: 8 }} />}
                  </div>
                  <div>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: '#e8e6e0', marginBottom: 6, letterSpacing: '-0.1px' }}>{event}</p>
                    <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, fontWeight: 300 }}>{detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* Founder */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 56 }}>The Team</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center', padding: '56px 52px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, background: 'rgba(255,255,255,0.025)' }}>
              <div>
                <div style={{ width: 52, height: 52, borderRadius: 13, background: '#e8e6e0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 20, fontWeight: 700, color: '#000' }}>M</span>
                </div>
                <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 700, letterSpacing: '-0.8px', color: '#e8e6e0', marginBottom: 6 }}>Martin W. David</h3>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.24)', fontWeight: 400, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 24 }}>Founder & CEO — Nairobi, Kenya</p>
                <a href="https://x.com/MinoMarkets254" target="_blank" rel="noopener noreferrer"
                  style={{ width: 32, height: 32, borderRadius: 7, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="rgba(255,255,255,0.4)">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.735-8.849L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
              <div>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, color: 'rgba(255,255,255,0.36)', lineHeight: 1.85, marginBottom: 18, fontWeight: 300 }}>
                  Martin built Mino entirely from scratch — frontend, backend, payments, market mechanics, and infrastructure — as a solo developer at 19.
                </p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, color: 'rgba(255,255,255,0.36)', lineHeight: 1.85, fontWeight: 300 }}>
                  The platform processes real M-PESA transactions and serves live prediction markets to users across Kenya.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>

        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
        </div>

        {/* CTA row */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '100px 48px' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div onClick={() => navigate('/markets')} style={{ background: '#e8e6e0', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#000', marginBottom: 10, letterSpacing: '-0.5px' }}>Browse the markets</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.45)', lineHeight: 1.6, marginBottom: 24, fontWeight: 400 }}>See what's live and place your first prediction.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Go to markets
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
              <div onClick={() => navigate('/investors')} style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', background: 'rgba(255,255,255,0.025)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.5px' }}>Partner with us</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, marginBottom: 24, fontWeight: 300 }}>We're early-stage and open to investors who believe in Africa.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Investor relations
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · DAFCOM ENTERPRISES</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}